import './styles/menupage.css';
import { useShallow } from 'zustand/shallow';
import { useGameStore } from '../../store/gameStore';
import { menuAnimationDelay } from '../utils/animationHelper';
import TitleLogo from '../components/TitleLogo';
import Btn from '../subcomponents/Btn';
import Switch from '../subcomponents/Switch';
import IconBtn from '../subcomponents/IconBtn';
import SettingsDialog from '../components/dialogs/SettingsDialog';
import HowToPlayDialog from '../components/dialogs/HowToPlayDialog';
import WelcomeDialog from '../components/dialogs/WelcomeDialog';
import { useEffect, useState } from 'react';
import StatisticsDialog from '../components/dialogs/StatisticsDialog';
import SupportDialog from '../components/dialogs/SupportDialog';
import LeaderboardDialog from '../components/dialogs/LeaderboardDialog';
import { GameMode } from '@/types/types';

function MenuPage() {
  // Subscribe to game store
  const {
    setMode,
    difficulty,
    setDifficulty,
    setGameActive,
    hasPlayedBefore,
    setHasPlayedBefore,
    setWasReset,
  } = useGameStore(
    useShallow((state) => ({
      setMode: state.setMode,
      difficulty: state.difficulty,
      setDifficulty: state.setDifficulty,
      setGameActive: state.setGameActive,
      resetGame: state.resetGame,
      hasPlayedBefore: state.hasPlayedBefore,
      setHasPlayedBefore: state.setHasPlayedBefore,
      wasReset: state.wasReset,
      setWasReset: state.setWasReset,
    }))
  );

  // Handle refresh when game resets
  const [refreshKey, setRefreshKey] = useState(0);
  useEffect(() => {
    const handleVisibilityChange = () => {
      const { wasReset } = useGameStore.getState();
      if (document.visibilityState === 'visible' && wasReset) {
        setRefreshKey((prev) => prev + 1);
        useGameStore.getState().setWasReset(false);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const getGameMode = (mode: string): GameMode => {
    switch (difficulty) {
      case '4x4':
        return mode === 'blitz' ? 'blitzSimple' : 'endlessSimple';
      case '5x5':
        return mode === 'blitz' ? 'blitzDeluxe' : 'endlessDeluxe';
    }

    return 'blitzSimple';
  };

  const handleGameModeSelect = (mode: string) => {
    let selectedMode: GameMode = getGameMode(mode);

    setWasReset(false);

    const menuPageElement = document.querySelector('.menu-container');
    menuPageElement?.classList.remove('fade-in-left');
    menuPageElement?.classList.add('fade-out-left');
    setTimeout(() => {
      setMode(selectedMode);
      setGameActive(true);
    }, menuAnimationDelay);
  };

  const hasPlayed = (mode: string) => {
    let selectedMode: GameMode = getGameMode(mode);
    return useGameStore.getState().gameData[selectedMode].gameEnded;
  };

  // Dialog controllers
  const [showWelcome, setShowWelcome] = useState<boolean>(false);
  const [showHowToPlay, setShowHowToPlay] = useState<boolean>(false);
  const [showStatistics, setShowStatistics] = useState<boolean>(false);
  const [showLeaderboard, setShowLeaderboard] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState<boolean>(false);
  const [showSupport, setShowSupport] = useState<boolean>(false);

  // Handle welcome message
  const [hasHydrated, setHasHydrated] = useState(false);
  useEffect(() => {
    const checkHydration = () => {
      const hasHydrated = useGameStore.persist?.hasHydrated?.();
      if (hasHydrated) {
        setHasHydrated(true);
        return true;
      }
      return false;
    };

    if (checkHydration()) return;

    const interval = setInterval(() => {
      if (checkHydration()) {
        clearInterval(interval);
      }
    }, 50); // check every 50ms

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (hasHydrated && !hasPlayedBefore) {
      setShowWelcome(true);
    }
  }, [hasHydrated, hasPlayedBefore]);

  const handleCloseWelcome = () => {
    setHasPlayedBefore();
    setShowWelcome(false);
  };

  return (
    <div key={refreshKey} className="menu-wrapper">
      <TitleLogo />
      <div className="subtitle-text">Create as many words as possible.</div>
      <Switch options={['4x4', '5x5']} selected={difficulty} onChange={setDifficulty} />
      <div className="menu-button-container">
        <Btn
          text="BLITZ"
          xl
          appendIcon="Zap"
          yellowIcon
          showPlayedBadge={hasPlayed('blitz')}
          onClick={() => handleGameModeSelect('blitz')}
        />
        <Btn
          text="ENDLESS"
          xl
          showPlayedBadge={hasPlayed('endless')}
          onClick={() => handleGameModeSelect('endless')}
        />
      </div>
      <div className="menu-icon-row">
        <IconBtn icon="CircleHelp" xl onClick={() => setShowHowToPlay(true)} />
        <IconBtn
          icon="Star"
          xl
          onClick={() => {
            setShowStatistics(true);
          }}
        />
        <IconBtn icon="Award" xl onClick={() => setShowLeaderboard(true)} />
        <IconBtn icon="Settings" xl onClick={() => setShowSettings(true)} />
        <IconBtn icon="Heart" xl onClick={() => setShowSupport(true)} />
      </div>
      <WelcomeDialog
        open={showWelcome}
        onClose={() => {
          handleCloseWelcome();
        }}
      />
      <HowToPlayDialog
        open={showHowToPlay}
        onClose={() => {
          setShowHowToPlay(false);
        }}
      />
      <StatisticsDialog
        open={showStatistics}
        onClose={() => {
          setShowStatistics(false);
        }}
      />
      <LeaderboardDialog
        open={showLeaderboard}
        onClose={() => {
          setShowLeaderboard(false);
        }}
      />
      <SettingsDialog
        open={showSettings}
        onClose={() => {
          setShowSettings(false);
        }}
      />
      <SupportDialog
        open={showSupport}
        onClose={() => {
          setShowSupport(false);
        }}
      />
    </div>
  );
}

export default MenuPage;
